import React from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Paper,
  alpha,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  LabelList
} from "recharts";

const COLORS = {
  strength:   "#e07b39",
  attendance: "#4472c4",
};

// ─── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const attendance = payload.find((p) => p.dataKey === "attendance")?.value || 0;
  const remaining  = payload.find((p) => p.dataKey === "remaining")?.value  || 0;
  const strength   = attendance + remaining;
  const percentage = strength > 0 ? Math.round((attendance / strength) * 100) : 0;

  return (
    <Paper
      elevation={4}
      sx={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "12px 16px",
        border: "1px solid #BFDBFE",
        minWidth: "200px",
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 13,
          color: "#1E3A8A",
          mb: 1,
          borderBottom: "1px solid #BFDBFE",
          pb: 0.8,
        }}
      >
        {label}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: COLORS.strength }} />
          <Typography sx={{ fontSize: 12, color: "#334155" }}>Actual Strength:</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1E3A8A" }}>
          {strength.toLocaleString()}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: "3px", bgcolor: COLORS.attendance }} />
          <Typography sx={{ fontSize: 12, color: "#334155" }}>Attendance:</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#1E3A8A" }}>
          {attendance.toLocaleString()}
        </Typography>
      </Box>

      <Box sx={{ mt: 1, pt: 0.5, borderTop: "1px solid #BFDBFE" }}>
        <Typography sx={{ fontSize: 11, color: "#1E3A8A", fontWeight: 600 }}>
          Attendance Rate:{" "}
          <strong style={{ color: "#004AAD" }}>{percentage}%</strong>
        </Typography>
      </Box>
    </Paper>
  );
};

// ─── Custom Legend ─────────────────────────────────────────────────────────────
const CustomLegend = () => (
  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 2, justifyContent: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: COLORS.attendance }} />
      <Typography sx={{ fontSize: 12, color: "#475569" }}>Attendance</Typography>
    </Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: COLORS.strength }} />
      <Typography sx={{ fontSize: 12, color: "#475569" }}>Actual Strength</Typography>
    </Box>
  </Box>
);

// ─── Attendance label — always rendered ABOVE the bar ──────────────────────
const AttendanceLabel = (props) => {
  const { x, y, width, value } = props;
  if (!value) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 4}
      fill="#1a56db"
      fontSize={11}
      fontWeight={600}
      textAnchor="middle"
      dominantBaseline="auto"
    >
      {value.toLocaleString()}
    </text>
  );
};

// ─── Total strength + Percentage label — right of the full stacked bar ──────
const TotalLabel = (props) => {
  const { x, y, width, height, index, data } = props;
  const item = data?.[index];
  if (!item) return null;
  
  const total = item.attendance + item.remaining;
  if (!total) return null;
  
  const percentage = Math.round((item.attendance / total) * 100);
  
  return (
    <text
      x={x + width + 6}
      y={y + height / 2}
      fontSize={11}
      fontWeight={600}
      dominantBaseline="middle"
    >
      <tspan fill="#e07b39">{total.toLocaleString()}</tspan>
      <tspan fill="#000000"> ({percentage}%)</tspan>
    </text>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
export function EmployeeStrengthAttendanceChart({ allAttendance = [] }) {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const chartData = (allAttendance || [])
    .filter((item) => {
      const loc = item?.loc || "";
      return loc && loc !== "Total";
    })
    .map((item) => {
      const strength   = parseInt(item?.str || 0);
      const attendance = parseInt(item?.att || 0);
      const percentage = parseInt(item?.pre || 0);
      const location   = item?.loc || "";

      let type = "";
      switch (location) {
        case "E": type = "Executive";   break;
        case "S": type = "Supervisory"; break;
        case "C": type = "Clerical";    break;
        case "I": type = "Industrial";  break;
        case "T": type = "Trainee";     break;
        default:  type = location;
      }

      return {
        type,
        strength,
        attendance,
        remaining: strength - attendance,
        percentage,
      };
    });

  if (!chartData || chartData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          backgroundColor: "#F8FAFF",
          borderRadius: "20px",
          padding: "24px",
          border: "1px solid #BFDBFE",
          textAlign: "center",
        }}
      >
        <Typography sx={{ color: "#64748B" }}>No attendance data available</Typography>
      </Paper>
    );
  }

  const barSize     = isMobile ? 18 : 22;
  const chartHeight = chartData.length * (barSize + 34) + 80;
  const YAXIS_W     = isMobile ? 85 : 100;
  const RIGHT_MARGIN = isMobile ? 70 : 95;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, type: "spring", stiffness: 100 }}
    >
      <Paper
        elevation={0}
        sx={{
          overflow: "hidden",
          backgroundColor: "#F8FAFF",
          borderRadius: "20px",
          padding: { xs: "16px", sm: "20px", md: "24px" },
          border: "1px solid #BFDBFE",
          boxShadow: "0 4px 24px rgba(37,99,235,0.07)",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: { xs: "1rem", sm: "1.2rem" },
              fontWeight: 600,
              color: "#201f1f",
              letterSpacing: "-0.01em",
            }}
          >
            DGES Attendance Based on Category
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ width: "150%", height: chartHeight, minHeight: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 18, right: RIGHT_MARGIN, left: 0, bottom: 10 }}
              barCategoryGap={14}
              barGap={0}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#DBEAFE"
                horizontal={false}
                vertical={true}
              />

              <YAxis
                type="category"
                dataKey="type"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#1E3A8A", fontSize: isMobile ? 12 : 13, fontWeight: 600 }}
                width={YAXIS_W}
                interval={0}
              />

              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#94a3b8", fontSize: isMobile ? 10 : 11 }}
                tickFormatter={(v) => v.toLocaleString()}
                domain={[0, 'dataMax + 50']}
              />

              <RechartsTooltip
                content={<CustomTooltip />}
                cursor={{ fill: alpha("#0b0c0c", 0.05) }}
              />

              {/* ── Attendance segment (blue) ── */}
              <Bar
                dataKey="attendance"
                name="Attendance"
                fill={COLORS.attendance}
                radius={[0, 0, 0, 0]}
                barSize={barSize}
                stackId="stack"
              >
                <LabelList
                  dataKey="attendance"
                  content={<AttendanceLabel />}
                />
              </Bar>

              {/* ── Remaining segment (orange) ── */}
              <Bar
                dataKey="remaining"
                name="Actual Strength"
                fill={COLORS.strength}
                radius={[0, 5, 5, 0]}
                barSize={barSize}
                stackId="stack"
              >
                {/* Total strength + Percentage in one line */}
                <LabelList
                  dataKey="remaining"
                  content={(props) => <TotalLabel {...props} data={chartData} />}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>

        {/* Legend */}
        <CustomLegend />
      </Paper>
    </motion.div>
  );
}